import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { SimpleModal } from '@livable/saas-react-components';
import { Alert, Button, MenuItem, TextField, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { api } from 'src/api';
import { CurrentSubscriptionTier, useCurrentSubscriptionTier } from 'src/components/SubscriptionTier';
import { archivedSetups, liveSetups, setupTypes } from 'src/components/UtilitySetupSection/companySetups';
import { messageOf, sameId } from 'src/components/UtilitySetupSection/parts';
import { savedLine } from 'src/components/UtilitySetupSection/setupEditor';
import { UtilitySetupCreator } from 'src/components/UtilitySetupSection/UtilitySetupCreator';
import { UtilitySetupEditor } from 'src/components/UtilitySetupSection/UtilitySetupEditor';
import { useStore } from 'src/stores';
import { getCurrentCompanyId } from 'src/stores/billAutomation/currentCompanyId';
import { announceSetupChange, NO_OPTIONS, UTILITY_SETUP_CHANGED } from 'src/stores/utilitySetups/companySetupsRead';
import { SetupsTable } from './SetupsTable';
import useStyles from './styles';
const ALL_TYPES = '';
const NOT_READ = {
  key: null,
  status: 'loading',
  setups: [],
  refreshFailed: false
};

// A write's answer shows only on the company it was made for: a switch while it runs closes the dialogs, and its notice
// or refusal mustn't then land on the next company's page.
const stillOn = company => company === getCurrentCompanyId();

// A write in progress holds only the page of the company it runs for: one still running after a switch leaves the next
// company's page free. Clears the mark when it's still that write's.
const doneFor = company => current => current === company ? '' : current;

/**
 * The company's utility setups (LB-6193, PRD V3 FR-2 and FR-10), opened from the profile menu: each setup with its
 * values and how many utilities follow it, its pending edit and its history. A setup opens in the setup editor (LB-6189),
 * a new one can be made for a type the company has setups of, and one nothing follows can be archived.
 */
const Setups = observer(() => {
  var _useCurrentSubscripti, _useCurrentSubscripti2;
  const classes = useStyles();
  const store = useStore();
  const isAppfolio = !!store.company.companyFeatureStore.isAppfolio;
  const allows = (_useCurrentSubscripti = (_useCurrentSubscripti2 = useCurrentSubscriptionTier()) === null || _useCurrentSubscripti2 === void 0 ? void 0 : _useCurrentSubscripti2.allows) !== null && _useCurrentSubscripti !== void 0 ? _useCurrentSubscripti : null;
  // Observed, so a company switch re-renders this and reads the new company's setups. The company itself is
  // localStorage's, which every switch writes first.
  const companyAccessId = store.homeDashboardStore.companyAccessId;
  const companyId = getCurrentCompanyId();

  // status: 'loading' | 'ready' | 'failed' | 'forbidden'. A read again keeps the list on screen until it answers.
  const _useState = useState(NOT_READ),
    _useState2 = _slicedToArray(_useState, 2),
    read = _useState2[0],
    setRead = _useState2[1];
  const _useState3 = useState(0),
    _useState4 = _slicedToArray(_useState3, 2),
    version = _useState4[0],
    setVersion = _useState4[1];
  const _useState5 = useState(0),
    _useState6 = _slicedToArray(_useState5, 2),
    attempt = _useState6[0],
    setAttempt = _useState6[1];
  const _useState7 = useState(ALL_TYPES),
    _useState8 = _slicedToArray(_useState7, 2),
    typeId = _useState8[0],
    setTypeId = _useState8[1];
  const _useState9 = useState(false),
    _useState0 = _slicedToArray(_useState9, 2),
    showArchived = _useState0[0],
    setShowArchived = _useState0[1];
  const _useState1 = useState([]),
    _useState10 = _slicedToArray(_useState1, 2),
    openHistories = _useState10[0],
    setOpenHistories = _useState10[1];
  const _useState11 = useState(null),
    _useState12 = _slicedToArray(_useState11, 2),
    editing = _useState12[0],
    setEditing = _useState12[1];
  // The company each write in progress runs for, or ''.
  const _useState13 = useState(''),
    _useState14 = _slicedToArray(_useState13, 2),
    editorSavingFor = _useState14[0],
    setEditorSavingFor = _useState14[1];
  const _useState15 = useState(false),
    _useState16 = _slicedToArray(_useState15, 2),
    creating = _useState16[0],
    setCreating = _useState16[1];
  // { setup, saving, error }: the setup whose archiving is being confirmed.
  const _useState17 = useState(null),
    _useState18 = _slicedToArray(_useState17, 2),
    archiving = _useState18[0],
    setArchiving = _useState18[1];
  const _useState19 = useState(''),
    _useState20 = _slicedToArray(_useState19, 2),
    cancellingFor = _useState20[0],
    setCancellingFor = _useState20[1];
  const _useState21 = useState(''),
    _useState22 = _slicedToArray(_useState21, 2),
    notice = _useState22[0],
    setNotice = _useState22[1];
  const _useState23 = useState(''),
    _useState24 = _slicedToArray(_useState23, 2),
    error = _useState24[0],
    setError = _useState24[1];
  // The setup an archive is running for: a second click on Archive before the dialog shows it's busy sends nothing.
  const archivingSetup = useRef('');
  useEffect(() => {
    const readAgain = () => setVersion(value => value + 1);
    window.addEventListener(UTILITY_SETUP_CHANGED, readAgain);
    return () => window.removeEventListener(UTILITY_SETUP_CHANGED, readAgain);
  }, []);

  // Another company: nothing of the last one's stays open, or a dialog confirmed now would act on its setup.
  useEffect(() => {
    setTypeId(ALL_TYPES);
    setShowArchived(false);
    setOpenHistories([]);
    setEditing(null);
    setCreating(false);
    setArchiving(null);
    setNotice('');
    setError('');
  }, [companyId, companyAccessId]);
  useEffect(() => {
    if (!companyId) return undefined;
    let current = true;
    setRead(last => last.key === companyId ? last : _objectSpread(_objectSpread({}, NOT_READ), {}, {
      key: companyId
    }));
    (async () => {
      try {
        const resp = await api.UtilitySetups.getCompanySetups({
          companyId
        }, NO_OPTIONS);
        if (!current) return;
        if (!Array.isArray(resp === null || resp === void 0 ? void 0 : resp.response)) throw new Error('No setups answer');
        setRead({
          key: companyId,
          status: 'ready',
          setups: resp.response,
          refreshFailed: false
        });
      } catch (err) {
        var _err$response;
        if (!current) return;
        const status = err === null || err === void 0 || (_err$response = err.response) === null || _err$response === void 0 ? void 0 : _err$response.status;
        setRead(last => {
          if (status === 403) return _objectSpread(_objectSpread({}, NOT_READ), {}, {
            key: companyId,
            status: 'forbidden'
          });
          // A read again that fails keeps the list on screen, and says it may be out of date.
          if (last.key === companyId && last.status === 'ready') return _objectSpread(_objectSpread({}, last), {}, {
            refreshFailed: true
          });
          return _objectSpread(_objectSpread({}, NOT_READ), {}, {
            key: companyId,
            status: 'failed'
          });
        });
      }
    })();
    return () => {
      current = false;
    };
  }, [companyId, companyAccessId, version, attempt]);
  if (!companyId) {
    return /*#__PURE__*/React.createElement("div", {
      className: classes.pageWrapper
    }, /*#__PURE__*/React.createElement("div", {
      className: classes.contentWrapper
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "body2",
      component: "p"
    }, "Pick a company to see its utility setups.")));
  }
  const shown = read.key === companyId ? read : NOT_READ;
  const all = shown.setups;
  const types = setupTypes(all);
  // A type the list no longer has (its last setup was archived and hidden, say) reads as every type.
  const filter = types.some(type => sameId(type.id, typeId)) ? typeId : ALL_TYPES;
  const live = liveSetups(all, filter);
  const archived = archivedSetups(all, filter);
  const busy = editorSavingFor === companyId || cancellingFor === companyId || !!(archiving !== null && archiving !== void 0 && archiving.saving);
  const toggleHistory = setupId => setOpenHistories(open => open.includes(setupId) ? open.filter(id => id !== setupId) : [...open, setupId]);
  const handleCancelEdit = async setup => {
    const company = companyId;
    setCancellingFor(company);
    setError('');
    setNotice('');
    try {
      await api.UtilitySetups.cancelPendingEdit({
        setupId: setup.id
      }, NO_OPTIONS);
      announceSetupChange(null);
      if (!stillOn(company)) return;
      setNotice("Cancelled the edit scheduled for ".concat(setup.name, "."));
    } catch (err) {
      if (stillOn(company)) setError(messageOf(err, 'Could not cancel the edit.'));
    } finally {
      setCancellingFor(doneFor(company));
    }
  };
  const handleArchive = async () => {
    if (!archiving || archivingSetup.current === archiving.setup.id) return;
    const setup = archiving.setup;
    const company = companyId;
    archivingSetup.current = setup.id;
    setArchiving({
      setup,
      saving: true,
      error: ''
    });
    try {
      await api.UtilitySetups.archiveSetup({
        setupId: setup.id
      }, NO_OPTIONS);
      announceSetupChange(null);
      if (!stillOn(company)) return;
      setArchiving(null);
      setNotice("Archived ".concat(setup.name, "."));
    } catch (err) {
      if (stillOn(company)) {
        setArchiving({
          setup,
          saving: false,
          error: messageOf(err, 'Could not archive the setup.')
        });
      }
    } finally {
      if (archivingSetup.current === setup.id) archivingSetup.current = '';
    }
  };
  const actions = {
    onToggleHistory: toggleHistory,
    onEdit: setup => {
      setNotice('');
      setEditing(setup);
    },
    onArchive: setup => setArchiving({
      setup,
      saving: false,
      error: ''
    }),
    onCancelEdit: handleCancelEdit
  };
  const ready = shown.status === 'ready';
  return /*#__PURE__*/React.createElement("div", {
    className: classes.pageWrapper
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.pageHeader
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.title
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h3"
  }, "Utility setups"), /*#__PURE__*/React.createElement(CurrentSubscriptionTier, null)), /*#__PURE__*/React.createElement(Button, {
    variant: "contained",
    size: "small",
    onClick: () => {
      setNotice('');
      setCreating(true);
    },
    disabled: !ready || types.length === 0 || busy
  }, "New setup")), /*#__PURE__*/React.createElement("div", {
    className: classes.contentWrapper
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    component: "p",
    className: classes.lead
  }, "How your company handles each kind of utility. Every utility follows one setup, and editing a setup changes every utility that follows it."), shown.status === 'loading' && /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "light",
    component: "p"
  }, "Loading your utility setups\u2026"), shown.status === 'forbidden' && /*#__PURE__*/React.createElement(Alert, {
    severity: "info"
  }, "You can't manage your company's utility setups."), shown.status === 'failed' && /*#__PURE__*/React.createElement(Alert, {
    severity: "warning",
    action: /*#__PURE__*/React.createElement(Button, {
      size: "small",
      onClick: () => setAttempt(value => value + 1)
    }, "Retry")
  }, "Couldn't load your utility setups."), ready && /*#__PURE__*/React.createElement(React.Fragment, null, shown.refreshFailed && /*#__PURE__*/React.createElement(Alert, {
    severity: "warning",
    action: /*#__PURE__*/React.createElement(Button, {
      size: "small",
      onClick: () => setAttempt(value => value + 1)
    }, "Retry")
  }, "Couldn't refresh the list, so it may be out of date."), notice && /*#__PURE__*/React.createElement(Alert, {
    severity: "success",
    onClose: () => setNotice('')
  }, notice), error && /*#__PURE__*/React.createElement(Alert, {
    severity: "error",
    onClose: () => setError('')
  }, error), all.length === 0 ? /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    component: "p"
  }, "No setups yet. Each utility gets one when it's saved.") : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: classes.toolbar
  }, /*#__PURE__*/React.createElement(TextField, {
    select: true,
    id: "utility-setups-type",
    size: "small",
    label: "Utility type",
    value: filter
    // "All types" is the empty value, which a select hides unless told to show it.
    ,
    SelectProps: {
      displayEmpty: true
    },
    InputLabelProps: {
      shrink: true
    },
    onChange: event => setTypeId(event.target.value),
    className: classes.typeFilter
  }, /*#__PURE__*/React.createElement(MenuItem, {
    value: ALL_TYPES
  }, "All types"), types.map(type => /*#__PURE__*/React.createElement(MenuItem, {
    key: type.id,
    value: type.id
  }, type.name)))), live.length === 0 ? /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    component: "p"
  }, "No setups of this type in use. Its archived setups are under Show archived.") : /*#__PURE__*/React.createElement(SetupsTable, {
    setups: live,
    label: "Utility setups",
    isAppfolio: isAppfolio,
    allows: allows,
    companyId: companyId,
    version: version,
    actions: actions,
    openHistories: openHistories,
    busy: busy
  }), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "light",
    component: "p",
    className: classes.note
  }, "Setups were made at launch from utilities set up the same way, and named after their type for you to rename. A new utility links to the most-used setup for its type with the same settings, or to a new setup made from them. Editing a setup applies from the next billing month, or at once when none of its utilities has been billed."), archived.length > 0 && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Button, {
    size: "small",
    variant: "text",
    className: classes.archivedToggle,
    "aria-expanded": showArchived,
    onClick: () => setShowArchived(value => !value)
  }, showArchived ? 'Hide archived' : "Show archived (".concat(archived.length, ")")), showArchived && /*#__PURE__*/React.createElement(SetupsTable, {
    setups: archived,
    label: "Archived utility setups",
    isAppfolio: isAppfolio,
    allows: allows,
    companyId: companyId,
    version: version,
    actions: {
      onToggleHistory: toggleHistory
    },
    openHistories: openHistories,
    busy: busy
  }))))), /*#__PURE__*/React.createElement(UtilitySetupEditor, {
    key: "editor|".concat(companyId),
    setup: editing,
    open: !!editing,
    onClose: () => setEditing(null),
    onSavingChange: saving => setEditorSavingFor(saving ? companyId : doneFor(companyId)),
    onSaved: (result, _ref) => {
      var _result$setup$name, _result$setup;
      let appliedNow = _ref.appliedNow;
      if (!stillOn(companyId)) return;
      setNotice(savedLine(result, appliedNow, (_result$setup$name = result === null || result === void 0 || (_result$setup = result.setup) === null || _result$setup === void 0 ? void 0 : _result$setup.name) !== null && _result$setup$name !== void 0 ? _result$setup$name : editing === null || editing === void 0 ? void 0 : editing.name));
      setEditing(null);
    }
  }), /*#__PURE__*/React.createElement(UtilitySetupCreator, {
    key: "creator|".concat(companyId),
    companyId: companyId,
    setups: all,
    typeId: filter,
    open: creating,
    onClose: () => setCreating(false),
    onCreated: (setup, name) => {
      var _setup$name;
      if (!stillOn(companyId)) return;
      setCreating(false);
      // A filter on another type would hide the setup just made, so the list shows its type (LB-6195).
      if (filter !== ALL_TYPES && setup !== null && setup !== void 0 && setup.utilityTypeId && !sameId(setup.utilityTypeId, filter)) {
        var _types$find$id, _types$find;
        setTypeId((_types$find$id = (_types$find = types.find(type => sameId(type.id, setup.utilityTypeId))) === null || _types$find === void 0 ? void 0 : _types$find.id) !== null && _types$find$id !== void 0 ? _types$find$id : setup.utilityTypeId);
      }
      setNotice("Made ".concat((_setup$name = setup === null || setup === void 0 ? void 0 : setup.name) !== null && _setup$name !== void 0 ? _setup$name : name, ". Link a utility to it from that utility's Setup section."));
    }
  }), /*#__PURE__*/React.createElement(SimpleModal, {
    isOpen: !!archiving,
    handleClose: () => !(archiving !== null && archiving !== void 0 && archiving.saving) && setArchiving(null),
    title: archiving ? "Archive ".concat(archiving.setup.name, "?") : '',
    leftButton: /*#__PURE__*/React.createElement(Button, {
      onClick: () => setArchiving(null),
      variant: "text",
      disabled: !!(archiving !== null && archiving !== void 0 && archiving.saving)
    }, "Cancel"),
    rightButton: /*#__PURE__*/React.createElement(Button, {
      onClick: handleArchive,
      size: "small",
      variant: "contained",
      disabled: !!(archiving !== null && archiving !== void 0 && archiving.saving)
    }, "Archive")
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.dialogBody
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    component: "p"
  }, "It's no longer offered when linking a utility. Archived setups can't be brought back."), (archiving === null || archiving === void 0 ? void 0 : archiving.error) && /*#__PURE__*/React.createElement(Alert, {
    severity: "error"
  }, archiving.error))));
});

/** Not shown while isBillingOptionsEnabled is off: the route leads to the dashboard and reads nothing. */
export const UtilitySetupsPage = observer(() => {
  const store = useStore();
  // Safe to read during render: index.tsx awaits the flags before mounting the app, and an unfetched flag is off.
  if (!store.account.featureFlagsStore.getIsBillingOptionsEnabled) return /*#__PURE__*/React.createElement(Navigate, {
    to: "/",
    replace: true
  });
  return /*#__PURE__*/React.createElement(Setups, null);
});